import { z } from 'zod';
/**
 * Decorations of the world: trees, rocks, flowers, houses. Data, not code:
 * a prop is a sprite key and a footprint. A blocking prop adds its footprint
 * to the zone's collision (`buildCollisionGrid`), so the terrain under it
 * stays what it is — a tree stands ON the meadow, a house on the square.
 *
 * The footprint is a rectangle of tiles whose bottom row is the prop's tile:
 * `width` tiles centred on it (the extra tile to the right when even),
 * `depth` rows up from it. A house's roof overhangs its footprint, so a
 * player walks behind the roof and in front of the wall, sorted by base.
 */
export const PropDefinitionSchema = z.object({
    id: z.string().min(1),
    /** Sprite key in the manifest (`prop.<name>`). */
    sprite: z.string().min(1),
    /** Whether the footprint is closed to walking. */
    blocks: z.boolean(),
    width: z.number().int().min(1).max(16).default(1),
    depth: z.number().int().min(1).max(16).default(1),
    /**
     * Where the sprite sits on its footprint: `base` stands it on the bottom
     * row (trees, houses: they rise above it); `centre` lays it over the whole
     * footprint (a bridge lies flat across the river).
     */
    anchor: z.enum(['base', 'centre']).default('base'),
    /**
     * Tiles the sprite rises from its base, roof included, when it is taller
     * than its footprint: the generator keeps these silhouettes from
     * overlapping, and build:atlas checks the sprite does not outgrow it. For
     * a `centre` prop, its whole height, spilling evenly above and below.
     */
    rise: z.number().int().min(1).max(12).optional(),
    /**
     * The way in through a blocking prop: these columns of its bottom row,
     * counted from its left edge, stay walkable. A dungeon's door is walked
     * into, like a cave's mouth; the zone's exit lies on it.
     */
    doorway: z.object({ from: z.number().int().min(0), width: z.number().int().min(1) }).optional(),
    /**
     * Lies on the ground though it blocks, like a hole: drawn under everyone
     * instead of sorted with the characters by its base.
     */
    flat: z.boolean().default(false),
});
const DEFINITIONS = [
    // Temperate: meadow and forest.
    { id: 'oak', sprite: 'prop.oak', blocks: true },
    { id: 'pine', sprite: 'prop.pine', blocks: true },
    { id: 'bush', sprite: 'prop.bush', blocks: true },
    { id: 'boulder', sprite: 'prop.boulder', blocks: true },
    { id: 'stump', sprite: 'prop.stump', blocks: true },
    { id: 'flowers', sprite: 'prop.flowers', blocks: false },
    { id: 'grass', sprite: 'prop.grass', blocks: false },
    { id: 'mushrooms', sprite: 'prop.mushrooms', blocks: false },
    // Swamp.
    { id: 'dead-tree', sprite: 'prop.dead-tree', blocks: true },
    { id: 'reeds', sprite: 'prop.reeds', blocks: false },
    // Desert.
    { id: 'cactus', sprite: 'prop.cactus', blocks: true },
    { id: 'palm', sprite: 'prop.palm', blocks: true },
    { id: 'dry-bush', sprite: 'prop.dry-bush', blocks: false },
    // Snow.
    { id: 'snowy-pine', sprite: 'prop.snowy-pine', blocks: true },
    // Villages. A house is seven tiles of facade by four of depth: its door
    // stands well above a person, its roof towers over the square.
    { id: 'house-red', sprite: 'prop.house-red', blocks: true, width: 7, depth: 4, rise: 6 },
    { id: 'house-blue', sprite: 'prop.house-blue', blocks: true, width: 7, depth: 4, rise: 6 },
    {
        id: 'house-forest-red',
        sprite: 'prop.house-forest-red',
        blocks: true,
        width: 7,
        depth: 4,
        rise: 6,
    },
    {
        id: 'house-forest-blue',
        sprite: 'prop.house-forest-blue',
        blocks: true,
        width: 7,
        depth: 4,
        rise: 6,
    },
    { id: 'house-swamp', sprite: 'prop.house-swamp', blocks: true, width: 7, depth: 4, rise: 6 },
    { id: 'adobe', sprite: 'prop.adobe', blocks: true, width: 7, depth: 4, rise: 5 },
    { id: 'cabin', sprite: 'prop.cabin', blocks: true, width: 7, depth: 4, rise: 6 },
    // What a village has besides homes: the inn, the chapel, the smithy, the
    // barn by the fields; the desert and the snow have their own inn.
    { id: 'tavern', sprite: 'prop.tavern', blocks: true, width: 7, depth: 4, rise: 8 },
    { id: 'tavern-forest', sprite: 'prop.tavern-forest', blocks: true, width: 7, depth: 4, rise: 8 },
    { id: 'tavern-swamp', sprite: 'prop.tavern-swamp', blocks: true, width: 7, depth: 4, rise: 8 },
    { id: 'chapel', sprite: 'prop.chapel', blocks: true, width: 7, depth: 4, rise: 7 },
    { id: 'forge', sprite: 'prop.forge', blocks: true, width: 7, depth: 4, rise: 6 },
    // The merchant's shop (ADR 0016), the same in every village.
    { id: 'shop', sprite: 'prop.shop', blocks: true, width: 7, depth: 4, rise: 7 },
    {
        id: 'auction-meadow',
        sprite: 'prop.auction-meadow',
        blocks: true,
        width: 7,
        depth: 4,
        rise: 7,
    },
    {
        id: 'auction-forest',
        sprite: 'prop.auction-forest',
        blocks: true,
        width: 7,
        depth: 4,
        rise: 7,
    },
    { id: 'auction-swamp', sprite: 'prop.auction-swamp', blocks: true, width: 7, depth: 4, rise: 7 },
    {
        id: 'auction-desert',
        sprite: 'prop.auction-desert',
        blocks: true,
        width: 7,
        depth: 4,
        rise: 7,
    },
    { id: 'auction-snow', sprite: 'prop.auction-snow', blocks: true, width: 7, depth: 4, rise: 7 },
    // The smelter's foundry (ADR 0018), the same in every village.
    { id: 'foundry', sprite: 'prop.foundry', blocks: true, width: 7, depth: 4, rise: 7 },
    // The alchemist's house (ADR 0026), the same in every village.
    { id: 'alchemy', sprite: 'prop.alchemy', blocks: true, width: 7, depth: 4, rise: 7 },
    // The same services use local materials in each outer biome. Their
    // footprints and south-facing doors stay identical for NPC placement.
    { id: 'forge-forest', sprite: 'prop.forge-forest', blocks: true, width: 7, depth: 4, rise: 6 },
    {
        id: 'foundry-forest',
        sprite: 'prop.foundry-forest',
        blocks: true,
        width: 7,
        depth: 4,
        rise: 7,
    },
    {
        id: 'alchemy-forest',
        sprite: 'prop.alchemy-forest',
        blocks: true,
        width: 7,
        depth: 4,
        rise: 7,
    },
    { id: 'shop-forest', sprite: 'prop.shop-forest', blocks: true, width: 7, depth: 4, rise: 7 },
    { id: 'forge-swamp', sprite: 'prop.forge-swamp', blocks: true, width: 7, depth: 4, rise: 6 },
    { id: 'foundry-swamp', sprite: 'prop.foundry-swamp', blocks: true, width: 7, depth: 4, rise: 7 },
    { id: 'alchemy-swamp', sprite: 'prop.alchemy-swamp', blocks: true, width: 7, depth: 4, rise: 7 },
    { id: 'shop-swamp', sprite: 'prop.shop-swamp', blocks: true, width: 7, depth: 4, rise: 7 },
    { id: 'forge-desert', sprite: 'prop.forge-desert', blocks: true, width: 7, depth: 4, rise: 6 },
    {
        id: 'foundry-desert',
        sprite: 'prop.foundry-desert',
        blocks: true,
        width: 7,
        depth: 4,
        rise: 7,
    },
    {
        id: 'alchemy-desert',
        sprite: 'prop.alchemy-desert',
        blocks: true,
        width: 7,
        depth: 4,
        rise: 7,
    },
    { id: 'shop-desert', sprite: 'prop.shop-desert', blocks: true, width: 7, depth: 4, rise: 7 },
    { id: 'forge-snow', sprite: 'prop.forge-snow', blocks: true, width: 7, depth: 4, rise: 6 },
    { id: 'foundry-snow', sprite: 'prop.foundry-snow', blocks: true, width: 7, depth: 4, rise: 7 },
    { id: 'alchemy-snow', sprite: 'prop.alchemy-snow', blocks: true, width: 7, depth: 4, rise: 7 },
    { id: 'shop-snow', sprite: 'prop.shop-snow', blocks: true, width: 7, depth: 4, rise: 7 },
    { id: 'barn', sprite: 'prop.barn', blocks: true, width: 7, depth: 4, rise: 7 },
    // The capital's stable (ADR 0047), out on the road, and its paddock's fence:
    // one tile a piece, the only blocking props allowed to touch (`FENCE_PROPS`).
    { id: 'stable', sprite: 'prop.stable', blocks: true, width: 7, depth: 4, rise: 5 },
    // Sablerouge's camel drivers' relay (ADR 0047), its camels loose around it.
    { id: 'camel-tent', sprite: 'prop.camel-tent', blocks: true, width: 7, depth: 4, rise: 6 },
    // The Givrecoeur Company's bastion at Havrebourg (ADR 0067, world-company.ts):
    // its keep, seen from the front like the houses; its walls, a tile a piece,
    // which touch one another (`FENCE_PROPS`); its gate, walked through; its
    // flagstones, flat under everything; and what stands in its yard.
    { id: 'company-hall', sprite: 'prop.company-hall', blocks: true, width: 7, depth: 4, rise: 8 },
    {
        id: 'company-gate',
        sprite: 'prop.company-gate',
        blocks: true,
        width: 3,
        rise: 4,
        doorway: { from: 1, width: 1 },
    },
    { id: 'company-wall-h', sprite: 'prop.company-wall-h', blocks: true, rise: 2 },
    { id: 'company-wall-h-banner', sprite: 'prop.company-wall-h-banner', blocks: true, rise: 2 },
    { id: 'company-wall-v', sprite: 'prop.company-wall-v', blocks: true, rise: 2 },
    { id: 'company-tower', sprite: 'prop.company-tower', blocks: true, rise: 3 },
    {
        id: 'company-yard',
        sprite: 'prop.company-yard',
        blocks: false,
        width: 15,
        depth: 14,
        anchor: 'centre',
    },
    {
        id: 'company-avenue',
        sprite: 'prop.company-avenue',
        blocks: false,
        width: 3,
        depth: 6,
        anchor: 'centre',
    },
    // The frozen flame: a sword planted in a block of ice, the fire alive inside.
    { id: 'company-monument', sprite: 'prop.company-monument', blocks: true, width: 3, rise: 4 },
    { id: 'company-brazier', sprite: 'prop.company-brazier', blocks: true, rise: 3 },
    {
        id: 'company-weapon-rack',
        sprite: 'prop.company-weapon-rack',
        blocks: true,
        width: 3,
        rise: 3,
    },
    { id: 'company-dummy', sprite: 'prop.company-dummy', blocks: true, rise: 3 },
    { id: 'company-crates', sprite: 'prop.company-crates', blocks: true, rise: 2 },
    { id: 'war-table', sprite: 'prop.war-table', blocks: true, width: 3, rise: 2 },
    { id: 'company-banner', sprite: 'prop.company-banner', blocks: true, rise: 4 },
    { id: 'fence-h', sprite: 'prop.fence-h', blocks: true },
    { id: 'fence-v', sprite: 'prop.fence-v', blocks: true },
    { id: 'fence-end', sprite: 'prop.fence-end', blocks: true },
    { id: 'caravanserai', sprite: 'prop.caravanserai', blocks: true, width: 7, depth: 4, rise: 6 },
    { id: 'longhouse', sprite: 'prop.longhouse', blocks: true, width: 7, depth: 4, rise: 6 },
    { id: 'well', sprite: 'prop.well', blocks: true, width: 2 },
    { id: 'stall', sprite: 'prop.stall', blocks: true, width: 2 },
    // The village's portal (ADR 0017): the stone arch at the heart of the square.
    { id: 'portal', sprite: 'prop.portal', blocks: true, width: 3, rise: 4 },
    // Farms on the outskirts: crops lie flat and are walked through.
    {
        id: 'field-wheat',
        sprite: 'prop.field-wheat',
        blocks: false,
        width: 5,
        depth: 3,
        anchor: 'centre',
    },
    {
        id: 'field-garden',
        sprite: 'prop.field-garden',
        blocks: false,
        width: 5,
        depth: 3,
        anchor: 'centre',
    },
    { id: 'haystack', sprite: 'prop.haystack', blocks: true },
    // Crossings: walked over, drawn under everyone.
    { id: 'bridge-h', sprite: 'prop.bridge-h', blocks: false, width: 5, anchor: 'centre' },
    // Stairs up a cliff (ADR 0020): two tiles wide across its straight south
    // face, the edge and the wall under it, one per ground.
    {
        id: 'stairs-grass',
        sprite: 'prop.stairs-grass',
        blocks: false,
        width: 2,
        depth: 2,
        anchor: 'centre',
    },
    {
        id: 'stairs-sand',
        sprite: 'prop.stairs-sand',
        blocks: false,
        width: 2,
        depth: 2,
        anchor: 'centre',
    },
    {
        id: 'stairs-snow',
        sprite: 'prop.stairs-snow',
        blocks: false,
        width: 2,
        depth: 2,
        anchor: 'centre',
    },
    {
        id: 'stairs-marsh',
        sprite: 'prop.stairs-marsh',
        blocks: false,
        width: 2,
        depth: 2,
        anchor: 'centre',
    },
    { id: 'bridge-v', sprite: 'prop.bridge-v', blocks: false, depth: 5, anchor: 'centre' },
    // Places of interest.
    { id: 'cave', sprite: 'prop.cave', blocks: true, width: 4 },
    // The ways up a mountain (ADR 0021): the cave mouth in the valley is
    // walked into, the ladder on the summit is climbed down; both are exits.
    { id: 'cave-entrance', sprite: 'prop.cave-entrance', blocks: false, width: 4, depth: 2 },
    { id: 'summit-hole', sprite: 'prop.summit-hole', blocks: false, width: 2, anchor: 'centre' },
    { id: 'cave-ladder', sprite: 'prop.cave-ladder', blocks: false, width: 2 },
    { id: 'cave-daylight', sprite: 'prop.cave-daylight', blocks: false, width: 2, anchor: 'centre' },
    // Its arch and fan of light spill a row above and below the exit (SHE-79).
    {
        id: 'cave-exit',
        sprite: 'prop.cave-exit',
        blocks: false,
        width: 2,
        anchor: 'centre',
        rise: 3,
    },
    // Inside the caves (ADR 0062): what rises from the floor, one tile each,
    // and what lies on it.
    { id: 'cave-stalagmite', sprite: 'prop.cave-stalagmite', blocks: true },
    { id: 'cave-stalagmite-ice', sprite: 'prop.cave-stalagmite-ice', blocks: true },
    { id: 'cave-stalagmite-sand', sprite: 'prop.cave-stalagmite-sand', blocks: true },
    { id: 'cave-crystal', sprite: 'prop.cave-crystal', blocks: true },
    { id: 'cave-crystal-amber', sprite: 'prop.cave-crystal-amber', blocks: true },
    { id: 'cave-crate', sprite: 'prop.cave-crate', blocks: true },
    { id: 'cave-torch', sprite: 'prop.cave-torch', blocks: true },
    { id: 'cave-glowcap', sprite: 'prop.cave-glowcap', blocks: false },
    { id: 'cave-puddle', sprite: 'prop.cave-puddle', blocks: false },
    { id: 'cave-rubble', sprite: 'prop.cave-rubble', blocks: false },
    { id: 'cave-cobweb', sprite: 'prop.cave-cobweb', blocks: false },
    { id: 'cave-moss', sprite: 'prop.cave-moss', blocks: false },
    { id: 'cave-slime', sprite: 'prop.cave-slime', blocks: false },
    { id: 'cave-ice', sprite: 'prop.cave-ice', blocks: false },
    { id: 'cave-skeleton', sprite: 'prop.cave-skeleton', blocks: false },
    // Monster camps (ADR 0021). The chest is drawn by the client from the
    // zone's `chests`, shut, chained or open; here only for its footprint.
    { id: 'tent', sprite: 'prop.tent', blocks: true, width: 3, depth: 2, rise: 3 },
    { id: 'campfire', sprite: 'prop.campfire', blocks: true, width: 2 },
    { id: 'chest', sprite: 'prop.chest', blocks: true },
    { id: 'chest-locked', sprite: 'prop.chest-locked', blocks: true },
    { id: 'chest-open', sprite: 'prop.chest-open', blocks: true },
    { id: 'ruins', sprite: 'prop.ruins', blocks: true, width: 3 },
    { id: 'tent', sprite: 'prop.tent', blocks: true, width: 3, depth: 2 },
    { id: 'campfire', sprite: 'prop.campfire', blocks: true },
    { id: 'menhir', sprite: 'prop.menhir', blocks: true },
    { id: 'tombstone', sprite: 'prop.tombstone', blocks: true },
    { id: 'obelisk', sprite: 'prop.obelisk', blocks: true, width: 2 },
    { id: 'tower', sprite: 'prop.tower', blocks: true, width: 3, depth: 2 },
    { id: 'ice-crystal', sprite: 'prop.ice-crystal', blocks: true },
    // The dragon's marks (ADR 0067). Burnt twins of the living trees and of
    // the farm's buildings, standing; the scorch, the claw's furrows, the
    // fused glass and the meltwater lie flat under everything. Smoke, embers,
    // steam and the boiling mud move (`props/<name>/<i>.png`).
    { id: 'charred-oak', sprite: 'prop.charred-oak', blocks: true },
    { id: 'charred-pine', sprite: 'prop.charred-pine', blocks: true },
    { id: 'charred-palm', sprite: 'prop.charred-palm', blocks: true },
    { id: 'burnt-barn', sprite: 'prop.burnt-barn', blocks: true, width: 7, depth: 4, rise: 6 },
    { id: 'burnt-house', sprite: 'prop.burnt-house', blocks: true, width: 7, depth: 4, rise: 5 },
    { id: 'burnt-ruins', sprite: 'prop.burnt-ruins', blocks: true, width: 3 },
    {
        id: 'burnt-field',
        sprite: 'prop.burnt-field',
        blocks: false,
        width: 5,
        depth: 3,
        anchor: 'centre',
    },
    { id: 'smoke-column', sprite: 'prop.smoke-column', blocks: true, rise: 4 },
    // The same smoke without its heap, laid just behind a caved-in roof: the
    // building is drawn over its foot, so it rises out of the hole.
    { id: 'smoke-plume', sprite: 'prop.smoke-plume', blocks: false },
    { id: 'smouldering-stump', sprite: 'prop.smouldering-stump', blocks: true },
    { id: 'embers', sprite: 'prop.embers', blocks: false },
    { id: 'carcass', sprite: 'prop.carcass', blocks: false, width: 2 },
    { id: 'hot-mud', sprite: 'prop.hot-mud', blocks: false, width: 2 },
    { id: 'glass-spike', sprite: 'prop.glass-spike', blocks: true },
    { id: 'melted-glacier', sprite: 'prop.melted-glacier', blocks: true, width: 2 },
    { id: 'steaming-rock', sprite: 'prop.steaming-rock', blocks: true },
    ...[
        ['scorch', 2, 2],
        ['scorch-streak', 3, 2],
        ['claw-crater', 10, 6],
        ['glass-shards', 2, 2],
        ['glass-pool', 3, 2],
        ['meltwater', 3, 2],
    ].map(([id, width, depth]) => ({
        id,
        sprite: `prop.${id}`,
        blocks: false,
        width,
        depth,
        anchor: 'centre',
    })),
    // Shefira's Aerie (ADR 0068). Its way in at the Breach: the chasm she tore
    // rising out of the glacier, seen from above like the ground, about as long
    // as she is wide. A hole: nobody walks in it, it is asked at from its rim.
    {
        id: 'shefira-crevasse',
        sprite: 'prop.shefira-crevasse',
        blocks: true,
        width: 15,
        depth: 5,
        anchor: 'centre',
        flat: true,
    },
    // The arena's rim of smoking black rock, and the vents that breathe embers
    // through its floor (`props/ember-vent/<i>.png`), walked over.
    { id: 'aerie-rock', sprite: 'prop.aerie-rock', blocks: true, width: 3, rise: 4 },
    { id: 'aerie-rock-small', sprite: 'prop.aerie-rock-small', blocks: true, rise: 2 },
    { id: 'ember-vent', sprite: 'prop.ember-vent', blocks: false },
    // The Company's stations at the arena's edge (ADR 0068 §5): Nella's
    // harpoon ballista, Ulfar's frost brazier (its blue fire moves), Maëlle's horn.
    { id: 'station-ballista', sprite: 'prop.station-ballista', blocks: true, width: 3, rise: 3 },
    { id: 'station-frost-brazier', sprite: 'prop.station-frost-brazier', blocks: true, rise: 3 },
    { id: 'station-horn', sprite: 'prop.station-horn', blocks: true, width: 2, rise: 3 },
    // The memorial at the Company's quarter, for whoever finished the story: a
    // stele per fallen recruit, and the frozen flame without its sword.
    { id: 'memorial-stele', sprite: 'prop.memorial-stele', blocks: true, rise: 3 },
    {
        id: 'company-monument-empty',
        sprite: 'prop.company-monument-empty',
        blocks: true,
        width: 3,
        rise: 3,
    },
    // Zone bosses' lairs (ADR 0024): a ring of braziers marks every one, the
    // rest says whose it is. The bones lie flat and are walked over.
    { id: 'brazier', sprite: 'prop.brazier', blocks: true },
    { id: 'bone-pile', sprite: 'prop.bone-pile', blocks: false },
    { id: 'crow-post', sprite: 'prop.crow-post', blocks: true },
    { id: 'skull-totem', sprite: 'prop.skull-totem', blocks: true },
    { id: 'jackal-statue', sprite: 'prop.jackal-statue', blocks: true },
    { id: 'ice-pillar', sprite: 'prop.ice-pillar', blocks: true },
    // Each boss's dungeon (ADR 0027): a fortress in its biome's style, seen
    // from the front like the houses, its dark gate two tiles wide in the
    // middle of its bottom row, walked into.
    ...['meadow', 'forest', 'swamp', 'desert', 'snow'].map((biome) => ({
        id: `dungeon-${biome}`,
        sprite: `prop.dungeon-${biome}`,
        blocks: true,
        width: 12,
        depth: 4,
        rise: 11,
        doorway: { from: 5, width: 2 },
    })),
];
export const PROPS = Object.fromEntries(DEFINITIONS.map((raw) => {
    const prop = PropDefinitionSchema.parse(raw);
    return [prop.id, prop];
}));
export const PROP_IDS = Object.keys(PROPS);
/** One prop standing on one tile of a zone. */
export const PropPlacementSchema = z.object({
    prop: z.string().refine((id) => id in PROPS, { message: 'Unknown prop' }),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** Drawn mirrored, so a row of the same tree does not read as stamped. */
    flip: z.boolean().default(false),
    /**
     * What stood here before the dragon burnt it (ADR 0067): a player whose
     * story has not reached `quest` sees `prop` instead, or nothing. The twin
     * keeps the footprint and the blocking (test), so collision is one for all.
     */
    before: z
        .object({
        quest: z.string().min(1),
        prop: z
            .string()
            .refine((id) => id in PROPS, { message: 'Unknown prop' })
            .nullable(),
    })
        .optional(),
});
/** The tiles a placed prop covers, bottom row on its own tile. */
/** Whether a tile of a placed prop's footprint is its doorway, walked through. */
export function inDoorway(placed, x, y) {
    const doorway = PROPS[placed.prop]?.doorway;
    if (doorway === undefined || y !== placed.y)
        return false;
    const from = footprintOf(placed).x0 + doorway.from;
    return x >= from && x < from + doorway.width;
}
/**
 * A paddock's fence and the stable it leans on (ADR 0047), and the walls of
 * the Company's bastion with their gate (ADR 0067): blocking props allowed to
 * touch one another, since the paddock and the bastion always keep their
 * gate open.
 */
export const FENCE_PROPS = new Set([
    'fence-h',
    'fence-v',
    'fence-end',
    'stable',
    'company-wall-h',
    'company-wall-h-banner',
    'company-wall-v',
    'company-tower',
    'company-gate',
]);
export function footprintOf(placed) {
    const definition = PROPS[placed.prop];
    const width = definition?.width ?? 1;
    const depth = definition?.depth ?? 1;
    const x0 = placed.x - Math.floor((width - 1) / 2);
    return { x0, y0: placed.y - depth + 1, x1: x0 + width - 1, y1: placed.y };
}
/**
 * The rows a prop's drawing covers over its footprint's columns: a `base`
 * prop rises `rise` rows from its bottom row, a `centre` prop spills its
 * extra height evenly above and below its footprint.
 */
export function silhouetteOf(placed) {
    const box = footprintOf(placed);
    const definition = PROPS[placed.prop];
    const depth = box.y1 - box.y0 + 1;
    const rise = Math.max(depth, definition?.rise ?? depth);
    if (definition?.anchor === 'centre') {
        const above = Math.floor((rise - depth) / 2);
        return { ...box, y0: box.y0 - above, y1: box.y1 + (rise - depth - above) };
    }
    return { ...box, y0: box.y1 - rise + 1 };
}
//# sourceMappingURL=props.js.map